/**
 * SaaS Onboarding Route - /onboarding
 *
 * Requires Clerk auth (gated in-component — see the auth gate below for why it
 * can't live in the layout). Redirects to /dashboard if the user already has an org,
 * UNLESS they opened the page in owner preview mode (?preview=1 + isOwner) —
 * then the wizard runs in sandbox mode and never writes to the DB on submit.
 */
import { createFileRoute, useNavigate, Navigate } from '@tanstack/react-router';
import { useRole } from '@/contexts/RoleContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { useClerkGate } from '@/hooks/useClerkGate';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SaaSOnboardingWizard } from '@/features/onboarding/SaaSOnboardingWizard';
import { LanguageToggle } from '@/components/theme/LanguageToggle';

interface OnboardingSearch {
  preview?: string;
}

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
  // TanStack Router JSON-parses each search value, so `?preview=1` arrives as
  // the NUMBER 1 — a string-only check silently drops it and the guard below
  // bounces owners to /dashboard. Accept both and normalize.
  validateSearch: (search: Record<string, unknown>): OnboardingSearch => ({
    preview:
      typeof search.preview === 'string' || typeof search.preview === 'number'
        ? String(search.preview)
        : undefined,
  }),
});

function OnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('onboarding');
  const { isLoaded: isClerkLoaded, isSignedIn } = useClerkGate();
  const { orgs, isLoading } = useRole();
  const { isOwner, isOwnerResolved } = usePermissions();
  const { preview } = Route.useSearch();
  const wantsPreview = preview === '1';
  const isPreview = isOwner && wantsPreview;

  // Guard runs only once role data has fully resolved. `isLoading` alone
  // isn't a safe enough gate for the "not an owner" branch below: TanStack
  // Query v5 derives its own isLoading as `isPending && isFetching`, so for
  // the single render right after Clerk finishes loading (the moment the
  // /me query's `enabled` flag first flips true), the fetch hasn't actually
  // started yet — it's kicked off from an effect that hasn't fired — and
  // isLoading reads false while `isOwner` is still stuck at its unresolved
  // `false` default. On a full-page preview load (?preview=1, the sidebar
  // link does a hard reload) that transient render lands the redirect below
  // before the real /me response ever arrives, bouncing the owner straight
  // to /dashboard. `isOwnerResolved` tracks the query's own success/error
  // state instead, which has no such gap.
  useEffect(() => {
    if (!isSignedIn) return; // handled by the auth gate below
    if (isLoading) return;
    if (isPreview) return; // valid owner preview — stay
    if (wantsPreview && !isOwner) {
      if (!isOwnerResolved) return; // isOwner not confirmed yet — wait for /me
      // preview requested but caller isn't an owner — bounce to dashboard
      void navigate({ to: '/dashboard' });
      return;
    }
    if (orgs.length > 0) {
      void navigate({ to: '/dashboard' });
    }
  }, [
    isSignedIn,
    isLoading,
    isPreview,
    wantsPreview,
    isOwner,
    isOwnerResolved,
    orgs,
    navigate,
  ]);

  // Auth gate. This route is dispatched to the ungated PublicLayout in
  // __root.tsx (alongside /terms and /privacy) to break a redirect loop:
  // MainAppLayout bounces a signed-in-but-org-less user *to* /onboarding, so
  // rendering the wizard inside that layout would ping-pong forever. Escaping
  // the layout also escaped its `!isSignedIn → SignInScreen` gate, which left
  // the wizard reachable by anonymous visitors. Re-gate it here instead — the
  // guard belongs to the route, not the layout.
  if (!isClerkLoaded) return null;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;

  if (isLoading) return null;
  if (wantsPreview && !isOwner) return null;
  if (!isPreview && orgs.length > 0) return null;

  return (
    <div className="min-h-screen bg-bg-void flex items-center justify-center p-4">
      <div className="max-w-xl w-full">
        {/* Language switch — safety net if auto-detection guessed wrong */}
        <div className="mb-4 flex justify-end">
          <LanguageToggle showLabel />
        </div>
        {isPreview && (
          <div className="mb-4 px-3 py-2 rounded-md border border-border bg-bg-elevated text-xs text-fg-muted text-center">
            {t('saas.previewBanner')}
          </div>
        )}
        <SaaSOnboardingWizard previewMode={isPreview} />
      </div>
    </div>
  );
}
